import React, { useContext, useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import {
  Contact,
  Address as SubscriptionAddress,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";
import {
  BillingFrequency,
  ContractStatus,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import ConfirmDialog from "../../../shared/confirm-dialog";
import SubscriptionAction from "./subscription-action";
import { createTrialSubscription } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import AddressForm from "components/shared/address/address-form";
import { contractBillingFreqToString } from "constants/contracts";
import { currencies } from "constants/currency";
import { OrgDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import {
  ENTRY_PERISCOPE,
  generateContractMetadata,
} from "helpers/contract-metadata";
import { useAddress } from "helpers/hooks/use-address";
import { toUUID } from "helpers/s12-helpers";
import { upCaseString } from "helpers/utils";
import { Address } from "types/address";
import {
  UpgradeSubWithoutCreditCardWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const displayBillingFrequencyOptions = () => {
  return [
    BillingFrequency.BILLING_FREQUENCY_MONTHLY,
    BillingFrequency.BILLING_FREQUENCY_ANNUAL,
  ].map((freq) => {
    return (
      <MenuItem
        key={`billing-frequency-${contractBillingFreqToString[
          freq
        ].toLowerCase()}`}
        value={freq}
        data-anchor={"billing-frequency-option"}
        data-testid={"billing-frequency-option"}
      >
        {upCaseString(contractBillingFreqToString[freq].toLowerCase())}
      </MenuItem>
    );
  });
};

const UpgradeWithoutCreditCard: React.FC<
  UpgradeSubWithoutCreditCardWrapperProps
> = ({ refreshOrgData }: UpgradeSubWithoutCreditCardWrapperProps) => {
  const theme = useTheme();
  const { regionCode } = useParams<UrlParamTypes>();
  const { orgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions } = useContext(PermissionsContext);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [isDialogueOpen, setisDialogueOpen] = useState(false);
  const [isPayloadValid, setIsPayloadValid] = useState(false);
  const [isSameAsBillingAddr, setIsSameAsBillingAddr] = useState(true);
  const [isSameShippingContact, setIsSameShippingContact] = useState(true);
  const [billingFreq, setBillingFreq] = useState(
    BillingFrequency.BILLING_FREQUENCY_MONTHLY,
  );
  const [formState, setFormState] = useState({
    firstName: "",
    lastName: "",
    email: "",
    orgName: "",
    billingPhone: "",
    billingCurrency: "",
  });
  const [shippingContact, setShippingContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [billingAddressState, setBillingAddressState] = useAddress({
    address: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
    address2: "",
  });
  const [shippingAddressState, setShippingAddressState] = useAddress({
    address: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
    address2: "",
  });
  const canUpdateCurrency = hasPermissions([
    ActionType.UPDATE_SUBSCRIPTION_CURRENCY,
  ]);

  const isActiveFreeOrg =
    orgData?.subscription?.[0]?.active &&
    (orgData?.subscription?.[0]?.type === SubscriptionType.FREE ||
      orgData?.subscription?.[0]?.type === SubscriptionType.FREE_ORG);

  const getDefaultCurrency = (country: string) => {
    let defaultCurrency: string;
    switch (country) {
      case "AU":
        defaultCurrency = "AUD";
        break;
      case "NZ":
        defaultCurrency = "AUD";
        break;
      default:
        switch (formState.billingCurrency) {
          case "GBP":
            defaultCurrency = "GBP";
            break;
          case "EUR":
            defaultCurrency = "EUR";
            break;
          default:
            defaultCurrency = "USD";
        }
    }
    return defaultCurrency;
  };

  useEffect(() => {
    setFormState({
      ...formState,
      firstName: "",
      lastName: "",
      orgName: orgData.name,
      email: orgData?.info?.email ? orgData?.info?.email : "",
      billingPhone: orgData?.info?.phone ? orgData?.info?.phone : "",
      billingCurrency: "",
    });
    setShippingContact({
      firstName: "",
      lastName: "",
      email: orgData?.info?.email ? orgData?.info?.email : "",
      phoneNumber: orgData?.info?.phone ? orgData?.info?.phone : "",
    });
    setBillingAddressState({
      ...billingAddressState,
      address: orgData?.info?.address?.street || "",
      postcode: orgData?.info?.address?.postalCode || "",
      city: orgData?.info?.address?.city || "",
      state: orgData.info?.address?.region || "",
      address2: orgData?.info?.address?.streetCont || "",
    });
    setShippingAddressState({
      ...shippingAddressState,
      address: orgData?.info?.address?.street || "",
      postcode: orgData?.info?.address?.postalCode || "",
      city: orgData?.info?.address?.city || "",
      state: orgData.info?.address?.region || "",
      address2: orgData?.info?.address?.streetCont || "",
    });
    // eslint-disable-next-line
  }, [orgData, isDialogueOpen]);

  useEffect(() => {
    if (!formState.email) {
      return setIsPayloadValid(false);
    }

    if (!formState.orgName) {
      return setIsPayloadValid(false);
    }

    if (!formState.firstName) {
      return setIsPayloadValid(false);
    }

    if (!formState.lastName) {
      return setIsPayloadValid(false);
    }

    if (!formState.billingCurrency) {
      return setIsPayloadValid(false);
    }

    setIsPayloadValid(true);
  }, [formState]);

  // Sync shipping contact with billing contact when checkbox is checked
  useEffect(() => {
    if (isSameShippingContact) {
      setShippingContact({
        firstName: formState.firstName || "",
        lastName: formState.lastName || "",
        email: formState.email || "",
        phoneNumber: formState.billingPhone || "",
      });
    }
  }, [formState, isSameShippingContact]);

  const onBillingAddressChange = (updatedBillingAddress: Address) => {
    setFormState({
      ...formState,
      ["billingCurrency"]: getDefaultCurrency(updatedBillingAddress.country),
    });
    setBillingAddressState(updatedBillingAddress);
    if (isSameAsBillingAddr) {
      setShippingAddressState({
        ...shippingAddressState,
        address: updatedBillingAddress.address || "",
        postcode: updatedBillingAddress.postcode || "",
        city: updatedBillingAddress.city || "",
        state: updatedBillingAddress.state || "",
        country: updatedBillingAddress.country || "",
        address2: updatedBillingAddress.address2 || "",
      });
    }
  };

  const onSameBillingAddrChange = (value: boolean) => {
    setIsSameAsBillingAddr(value);

    setShippingAddressState({
      ...shippingAddressState,
      address: billingAddressState.address || "",
      postcode: billingAddressState.postcode || "",
      city: billingAddressState.city || "",
      state: billingAddressState.state || "",
      country: billingAddressState.country || "",
      address2: billingAddressState.address2 || "",
    });
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
  ) => {
    setFormState({
      ...formState,
      [key]: event.target.value,
    });
  };

  const handleCurrencyChange = (currency: string) => {
    if (currency) {
      setFormState({
        ...formState,
        billingCurrency: currency,
      });
    }
  };

  const handleShippingContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof typeof shippingContact,
  ) => {
    setShippingContact({
      ...shippingContact,
      [key]: event.target.value,
    });
  };

  const onSameShippingContactChange = (value: boolean) => {
    setIsSameShippingContact(value);

    if (value) {
      // If checking the box, copy billing contact to shipping
      setShippingContact({
        firstName: formState.firstName || "",
        lastName: formState.lastName || "",
        email: formState.email || "",
        phoneNumber: formState.billingPhone || "",
      });
    }
    // If unchecking, leave shipping contact as is so user can edit independently
  };

  const handleUpgradeSubWithoutCreditCardReq = () => {
    setShowLoadingSpinner(true);

    const billingAddress = new SubscriptionAddress({
      thoroughfare: billingAddressState.address,
      locality: billingAddressState.city,
      postalCode: billingAddressState.postcode,
      administrativeArea: billingAddressState.state,
      country: billingAddressState.country,
    });
    if (billingAddressState.address2) {
      billingAddress.subThoroughfare = billingAddressState.address2;
    }
    const shippingAddress = new SubscriptionAddress({
      thoroughfare: shippingAddressState.address,
      locality: shippingAddressState.city,
      postalCode: shippingAddressState.postcode,
      administrativeArea: shippingAddressState.state,
      country: shippingAddressState.country,
    });
    if (shippingAddressState.address2) {
      shippingAddress.subThoroughfare = shippingAddressState.address2;
    }

    const metadata = generateContractMetadata({
      status: ContractStatus.TRIALING,
      entry: ENTRY_PERISCOPE,
    });

    const billingContact = new Contact({
      firstName: formState.firstName,
      lastName: formState.lastName,
      email: formState.email,
      phoneNumber: formState.billingPhone,
    });

    const shippingContactObj = new Contact({
      firstName: shippingContact.firstName,
      lastName: shippingContact.lastName,
      email: shippingContact.email,
      phoneNumber: shippingContact.phoneNumber,
    });

    createTrialSubscription(
      {
        orgId: toUUID(orgData.id),
        isDecoupledTrial: true,
        billingFrequency: billingFreq,
        legalName: formState.orgName,
        billingContact,
        billingAddress,
        shippingContact: shippingContactObj,
        shippingAddress,
        metadata,
        currency: formState.billingCurrency.toLowerCase(),
      },
      callOptionsHeaders(regionCode, orgData.id),
    )
      .then(() => {
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const getCountryInfoMessage = () => {
    if (formState.billingCurrency === "EUR") {
      return "This customer subscription will be billed in EUR";
    }

    if (formState.billingCurrency === "GBP") {
      return "This customer subscription will be billed in GBP";
    }

    let countryInfoMessage: string;
    switch (billingAddressState.country) {
      case "AU":
        countryInfoMessage = "AUD and charged GST";
        break;
      case "NZ":
        countryInfoMessage = "AUD";
        break;
      case "US":
        countryInfoMessage = "USD and charged Sales Tax";
        break;
      default:
        countryInfoMessage = "USD";
    }

    return `This customer subscription will be billed in ${countryInfoMessage}.`;
  };

  const cancelChanges = () => {
    setisDialogueOpen(false);
    setFormState({
      firstName: "",
      lastName: "",
      email: "",
      orgName: "",
      billingPhone: "",
      billingCurrency: "",
    });
    setShippingContact({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    });
    setBillingAddressState({});
    setBillingFreq(BillingFrequency.BILLING_FREQUENCY_MONTHLY);
  };

  const infoContainerSx = {
    background: "#DFF8FE",
    border: "1px solid #0066AF",
    color: "#0066AF",
    borderRadius: 1,
    minHeight: 80,
    height: "100%",
    padding: theme.spacing(2),
  };

  const countryContainerSx = {
    background: "#fff5d4",
    border: "1px solid #94671c",
    color: "#94671c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const displayAlertBanner = () => {
    return (
      <Grid item xs={12} style={{ marginTop: "15px", marginBottom: "10px" }}>
        <Box width={1} sx={infoContainerSx}>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            <Typography component={"span"}>
              Ensure that the billing details of the organisation is entered.
            </Typography>
          </ListItem>
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <SubscriptionAction title={"Upgrade to Premium"}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="upgrade-to-prem-btn"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isActiveFreeOrg}
            onClick={() => setisDialogueOpen(true)}
          >
            Upgrade without Credit Card
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title={"Upgrade to Premium"}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
        open={isDialogueOpen}
        setOpen={setisDialogueOpen}
        onConfirm={() => {
          setisDialogueOpen(false);
          handleUpgradeSubWithoutCreditCardReq();
        }}
        disabled={!isPayloadValid}
        onCancel={() => {
          cancelChanges();
        }}
      >
        <Grid container spacing={1}>
          {displayAlertBanner()}
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Billing Frequency</InputLabel>
              <Select
                id="upgrade-to-prem-billing-freq-select"
                data-anchor="upgrade-to-prem-billing-freq-select"
                data-testid="upgrade-to-prem-billing-freq-select"
                inputProps={{
                  "data-testid": "upgrade-to-prem-billing-freq-select-value",
                }}
                fullWidth
                label="Billing Frequency"
                required
                value={billingFreq}
                onChange={(event) => {
                  setBillingFreq(
                    event.target.value as
                      | BillingFrequency.BILLING_FREQUENCY_MONTHLY
                      | BillingFrequency.BILLING_FREQUENCY_ANNUAL,
                  );
                }}
              >
                {displayBillingFrequencyOptions()}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              name="orgName"
              label="Organisation name"
              inputProps={{ "data-testid": "upgrade-to-prem-org-name-field" }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "orgName")}
              defaultValue={formState.orgName}
              error={!formState.orgName}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">Billing Contact</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="firstName"
              label="First name"
              inputProps={{
                "data-testid": "upgrade-to-prem-first-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "firstName")}
              value={formState.firstName}
              error={!formState.firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="lastName"
              label="Last name"
              inputProps={{
                "data-testid": "upgrade-to-prem-last-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "lastName")}
              value={formState.lastName}
              error={!formState.lastName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="email"
              label="Email address"
              inputProps={{
                "data-testid": "upgrade-to-prem-org-email-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "email")}
              defaultValue={formState.email}
              error={!formState.email}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="phone"
              label="Phone number"
              inputProps={{
                "data-testid": "upgrade-to-prem-org-phone-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "billingPhone")}
              value={formState.billingPhone}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">Billing Address</Typography>
          </Grid>
          <AddressForm
            address={billingAddressState}
            onAddressChange={onBillingAddressChange}
            countryCodeFilter={["RU"]}
          />
          {billingAddressState.country && (
            <Grid item xs={6}>
              Default Currency {formState.billingCurrency}
              <Autocomplete
                options={currencies}
                autoHighlight
                autoSelect
                disableClearable
                data-testid={"upgrade-to-prem-currency-selection"}
                value={{
                  label: formState.billingCurrency,
                  value: formState.billingCurrency,
                }}
                disabled={
                  !canUpdateCurrency ||
                  formState.billingCurrency === "AUD" ||
                  billingAddressState.country === ""
                }
                getOptionLabel={(option) => option.label}
                onChange={(_event, currency) => {
                  handleCurrencyChange(currency.value);
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span>{option.label}</span>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={!formState.billingCurrency}
                    label="Currency"
                    name="billingCurrency"
                    variant="outlined"
                    margin="normal"
                    inputProps={{
                      ...params.inputProps,
                      "data-testid": "upgrade-to-prem-currency-field",
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {billingAddressState.country && (
            <Box width={1}>
              <Typography
                align="center"
                variant="body2"
                sx={countryContainerSx}
              >
                {getCountryInfoMessage()}
              </Typography>
            </Box>
          )}
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Contact</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSameShippingContact}
                  onChange={(_event, v) => {
                    onSameShippingContactChange(v);
                  }}
                  data-testid="upgrade-to-prem-same-shipping-contact-checkbox"
                />
              }
              label="Same as billing contact"
              labelPlacement="end"
            />
          </Grid>
          {!isSameShippingContact && (
            <>
              <Grid item xs={6}>
                <TextField
                  name="firstName"
                  label="First name"
                  inputProps={{
                    "data-testid": "upgrade-to-prem-shipping-first-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "firstName")}
                  value={shippingContact.firstName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="lastName"
                  label="Last name"
                  inputProps={{
                    "data-testid": "upgrade-to-prem-shipping-last-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "lastName")}
                  value={shippingContact.lastName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="email"
                  label="Email address"
                  inputProps={{
                    "data-testid": "upgrade-to-prem-shipping-email-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "email")}
                  value={shippingContact.email}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="phone"
                  label="Phone number"
                  inputProps={{
                    "data-testid": "upgrade-to-prem-shipping-phone-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) =>
                    handleShippingContactChange(e, "phoneNumber")
                  }
                  value={shippingContact.phoneNumber}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Address</Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSameAsBillingAddr}
                  onChange={(_event, v) => {
                    onSameBillingAddrChange(v);
                  }}
                />
              }
              label={"Same as billing address"}
              labelPlacement="end"
            />
          </Grid>
          {!isSameAsBillingAddr && (
            <Grid item xs={12}>
              <AddressForm
                address={shippingAddressState}
                onAddressChange={setShippingAddressState}
                countryCodeFilter={["RU"]}
              />
            </Grid>
          )}
        </Grid>
      </ConfirmDialog>
    </>
  );
};

export default UpgradeWithoutCreditCard;
